import { useEffect, useState } from "react";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useRelistingRules from "../../data/useRelistingRules";
import useCategoriesPartial from "../../data/useCategoriesPartial";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import { NumberInput } from "../../components/shared/Inputs/NumberInput";
import PopOutBox from "../../components/shared/PopOutBox";
import { useForm } from "react-hook-form";
import Card from "../../components/shared/Card/Card";
import Modal from "../../layouts/Modal";
import SelectableTable from "../../components/shared/Tables/SelectableTable";
import { CreateUpdateRelistingRuleRequest, IRelistingRule } from "../../models/RelistingRule";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";

export default function RelistingRules() {
  const { setInfo } = usePageNameContext();

  useEffect(() => {
    setInfo({
      name: "Automatic Relisting Rules",
      desc: "Manage rules for categories to automatically relist or reduce",
    });
  }, []);

  return (
    <div className="mt-4">
      <RulesTab />
    </div>
  );
}

function RulesTab() {
  const [selected, setSelected] = useState<IRelistingRule | undefined>(undefined);
  const { relistingRules, create, update, remove, setCategories, ruleCategories } = useRelistingRules(selected?.id);
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Relisting Rule");
  const { register, handleSubmit, reset, setValue, watch } = useForm<CreateUpdateRelistingRuleRequest>({
    defaultValues: { name: "", useReduction: true, useMaxDiscount: false, maximumDiscountPercentage: undefined, minimumPrice: undefined, isFixedReduction: false, reduction: 0, reductionInterval: 30, appendSkuSuffix: false },
  });

  const { categories: allCategories } = useCategoriesPartial();
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  const [selectedCategoryRows, setSelectedCategoryRows] = useState<string[]>([]);

  useEffect(() => {
    if (selected) {
      reset({
        name: selected.name ?? "",
        useReduction: selected.useReduction,
        useMaxDiscount: selected.useMaxDiscount,
        maximumDiscountPercentage: selected.maximumDiscountPercentage ?? undefined,
        minimumPrice: selected.minimumPrice ?? undefined,
        isFixedReduction: selected.isFixedReduction,
        reduction: selected.reduction ?? undefined,
        reductionInterval: selected.reductionInterval,
        appendSkuSuffix: selected.appendSkuSuffix ?? false,
      });
    } else {
      reset({ name: "", useReduction: false, useMaxDiscount: false, maximumDiscountPercentage: undefined, minimumPrice: undefined, isFixedReduction: false, reduction: undefined, reductionInterval: 30, appendSkuSuffix: false });
    }
  }, [selected]);

  const onSubmit = (data: CreateUpdateRelistingRuleRequest) => {
    // Coerce numeric fields: empty string => undefined (so API receives nullables)
    const coerce = (v: any) => (v === "" || v === null ? undefined : typeof v === "string" ? Number(v) : v);
    data.maximumDiscountPercentage = coerce(data.maximumDiscountPercentage) as any;
    data.minimumPrice = coerce(data.minimumPrice) as any;
    data.reduction = coerce(data.reduction) as any;

    if (!data.name) {
      const reductionText = data.useReduction ? `${data.isFixedReduction ? `-£${data.reduction}` : `-${data.reduction}%`}` : "Relist Only";
      data.name = `${reductionText}${data.useMaxDiscount && data.maximumDiscountPercentage ? ` (Max ${data.maximumDiscountPercentage}% off)` : ""}${data.minimumPrice ? `, Min £${data.minimumPrice}` : ""}`;
    }

    if (selected) {
      update.mutate({ id: selected.id, req: data }, {
        ...saveHandlers,
        onSettled: () => reset(data)
      });
    } else {
      create.mutate(data, {
        ...saveHandlers,
        onSuccess() {
          saveHandlers.onSuccess();
          reset();
        }
      });
    }
  };

  const openCategoriesModal = () => {
    if (!selected?.id) return;
    const existing = ruleCategories.data?.data ?? [];
    setSelectedCategoryRows([...(existing as string[])]);
    setCategoriesModalOpen(true);
  };

  const applyCategories = () => {
    if (!selected?.id) return;
    setCategories.mutate({ id: selected.id, categoryIds: selectedCategoryRows }, saveHandlers);
    setCategoriesModalOpen(false);
  };

  const onDelete = (rule: IRelistingRule) => {
    remove.mutate(rule.id, deleteHandlers);
    setSelected(undefined);
  };

  const useReduction = watch("useReduction");
  const useMaxDiscount = watch("useMaxDiscount");
  const isFixedReduction = watch("isFixedReduction");

  if (relistingRules.isLoading) return <LoadingWheel />;

  return (
    <>
      <Modal open={categoriesModalOpen} setOpen={setCategoriesModalOpen} width="sm:max-w-3xl">
        <div className="sm:flex sm:items-start">
          <div className="mt-3 text-left sm:mt-0 sm:ml-2 sm:text-left w-full">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Select Categories</h3>
            <SelectableTable
              options={(allCategories.data?.data ?? []).map(c => ({ id: c.id, name: c.name }))}
              isLoading={allCategories.isLoading}
              selectedIds={selectedCategoryRows}
              setSelectedIds={setSelectedCategoryRows}
              placeholder="Search categories"
            />
          </div>
        </div>
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <ButtonInput label={"Apply Selected"} isSubmit={false} onClick={applyCategories} classes="sm:ml-3 sm:w-auto w-full px-4" />
          <ButtonInput label={"Cancel"} isSubmit={false} onClick={() => setCategoriesModalOpen(false)} classes="mt-3 sm:mt-0 sm:w-auto w-full bg-white !text-gray-700 ring-1 ring-inset ring-gray-300 hover:!bg-gray-50 px-4" />
        </div>
      </Modal>

      <div className="mb-4 flex justify-between text-gray-600">
        <p>After selecting categories, an automated job will evaluate relisting and reductions.</p>
        {/* <p>Next Run: 01:00</p> */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        <Card title="Rules">
          <div className="min-h-[25vh]">
            <button
              className={"w-full text-left px-3 py-2 hover:bg-gray-100"}
              onClick={() => setSelected(undefined)}
            >
              + Create New Rule
            </button>

            {relistingRules.data?.data?.map((r) => (
              <button
                key={r.id}
                className={"w-full text-left px-3 py-2 hover:bg-gray-100" + (selected?.id === r.id ? " bg-blue-50" : "")}
                onClick={() => setSelected(r)}
              >
                {r.name}
              </button>
            ))}
          </div>
        </Card>

        <Card title="Rule Details" containerClassName="lg:col-span-2">
          <form className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
            <TextInput label="Rule Name" register={register} registerName="name" cols="sm:col-span-2" />

            <div className="flex items-center gap-x-4">
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                <span>Use Reduction</span>
                <PopOutBox classes="p-2 w-64">
                  <div className="text-xs text-gray-700">If turned off then this rule will only relist, not perform any price changes.</div>
                </PopOutBox>
              </label>
              <input type="checkbox" {...register("useReduction")} />
            </div>

            <div className="flex items-center gap-x-4">
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                <span>Append SKU</span>
                <PopOutBox classes="p-2 w-64">
                  <div className="text-xs text-gray-700">Relisted listings are published with a "-r1", "-r2", etc. suffix on the SKU to improve marketplace visibility. Tag numbers and scanning are unaffected.</div>
                </PopOutBox>
              </label>
              <input type="checkbox" {...register("appendSkuSuffix")} />
            </div>

            <hr className="sm:col-span-2 my-2" />

            <fieldset disabled={!useReduction} className="contents">
              <div className="flex items-center gap-x-4">
                <label className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                  <span>Use Max Discount %</span>
                  <PopOutBox classes="ml-1 p-2 w-64">
                    <div className="text-xs text-gray-700">
                      <p>Apply a maximum discount cap when reducing, or reduce until a minimum price.</p>
                      <br />
                      <p>e.g. If the original listing price is £100:</p>
                      <p>Maximum Discount: If the maximum discount is 50% the price won't go lower than £50.</p>
                      <p>Minimum Price: If the minimum price is set to £60 it won't lower than £60.</p>
                    </div>
                  </PopOutBox>
                </label>
                <input type="checkbox" className="ml-2" {...register("useMaxDiscount")} />
              </div>

              {useMaxDiscount ? (
                <div className="sm:col-span-1">
                  <label className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                    <span>Maximum Discount %</span>
                  </label>
                  <NumberInput label="" register={register} registerName="maximumDiscountPercentage" step=".01" disabled={!useReduction} />
                </div>
              ) : (
                <div className="sm:col-span-1">
                  <label className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                    <span>Minimum Price (£)</span>
                  </label>
                  <NumberInput label="" register={register} registerName="minimumPrice" step=".01" disabled={!useReduction} />
                </div>
              )}

              <div className="flex items-center gap-x-4">
                <label className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                  <span>Fixed Reduction</span>
                  <PopOutBox classes="ml-1 p-2 w-64">
                    <div className="text-xs text-gray-700">If checked, reduction is a fixed £ value. Otherwise percentage.</div>
                  </PopOutBox>
                </label>
                <input type="checkbox" className="ml-2" {...register("isFixedReduction")} />
              </div>

              <div className="sm:col-span-1">
                <label className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                  {isFixedReduction ? (
                    <span className="font-bold">£</span>
                  ) : (
                    <span className="font-bold">%</span>
                  )}
                  <span>Reduction</span>
                </label>
                <NumberInput label="" register={register} registerName="reduction" step=".01" disabled={!useReduction} />
              </div>

              <div className="sm:col-span-1">
                <label className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                  <span>Reduction Interval</span>
                </label>
                <select
                  {...register("reductionInterval", { valueAsNumber: true })}
                  disabled={!useReduction}
                  className={"w-full mt-1 text-gray-900 border-1 border-gray-400 px-3 py-2 text-sm"}
                >
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                  <option value={90}>90 days</option>
                </select>
              </div>
            </fieldset>

            {selected && (
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-700">Categories</div>
                    <div className="text-xs text-gray-500">{ruleCategories.data?.data?.length ?? 0} selected</div>
                  </div>
                  <ButtonInput label={"Edit Categories"} isSubmit={false} isLoading={setCategories.isLoading} onClick={openCategoriesModal} classes="" />
                </div>
              </div>
            )}


            <div className="sm:col-span-2 flex items-center justify-between">
              <ButtonInput label={selected ? "Save" : "Create"} isLoading={create.isLoading || update.isLoading} isSubmit={true} onClick={undefined} classes="" />

              {selected && (
                <ButtonInput label={"Delete"} isSubmit={false} onClick={() => onDelete(selected)} classes="bg-red-600 hover:bg-red-700" />
              )}
            </div>
          </form>
        </Card>
      </div>
    </>
  );
}


